import { useEffect, useMemo, useRef, useState } from "react";
import { useSurveyStore } from "../state/surveyStore";
import { useChatStore } from "../state/chatStore";
import { buildTurnMetrics } from "../observability/turnMetrics";
import { getMetrics } from "../observability/metrics";
import { saveCompletion } from "../state/completion";
import { clearStorage } from "../state/persistence";
import { loadSurvey } from "../survey/loader";
import type { Question, Survey } from "../survey/schema";
import { questionText } from "../survey/questionText";
import { structure } from "../api/structureClient";
import type { IntentLabel, StructureResult, AnswerVerdictLabel } from "../api/structureClient";
import { ChatHeader } from "./ChatHeader";
import { VoiceStage } from "./VoiceStage";
import { ConversationLog } from "./ConversationLog";
import { ReviewForm, type ReviewSubmission } from "./ReviewForm";
import { Completion } from "./Completion";
import type { MicState } from "./MicButton";
import type { Phase } from "../state/chatPhase";
import { AppError } from "../errors/types";
import { TTS_PHRASES } from "../audio/ttsPhrases";
import { identityProvider, sessionUploader } from "../storage/sessionUploaderInstance";
import type { AnswerEntry, AnswersArtifact } from "../storage/types";
import { buildPauseAck } from "../lib/ssml";
import { buildKeytermsForQuestion } from "../audio/sttKeyterms";
import { resolveSttProvider } from "../api/sttClientFactory";
import { redemptionMsForQuestionType } from "../audio/endpointing";
import { judgeInterruption } from "../audio/interruptionJudge";
import { judgeAnswer } from "../audio/answerJudge";
import type { AnswerRejectReason } from "../audio/answerJudge";
import { encodeWavPcm16 } from "../audio/wavEncoder";
import { useContinuousMode } from "../hooks/useContinuousMode";
import { useNarration } from "../hooks/useNarration";
import { useVisibilityPause } from "../hooks/useVisibilityPause";
import { useProcessingCue } from "../hooks/useProcessingCue";
import { useFollowBottom } from "../hooks/useFollowBottom";
import { VadDebugOverlay } from "./VadDebugOverlay";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { readPreviousBeaconFailed } from "../storage/sessionEventBuffer";
import { sessionEventBuffer } from "../storage/sessionEventBufferInstance";

const RESUME_PROMPT_QID = "__resume-prompt__";
const SUMMARY_QID = "__summary__";
// 完了の読み上げ用の擬似 messageId。chatStore の messages には積まない
// (完了画面ではスレッドを描画しないため、読み上げのためだけに状態を汚さない)。
const COMPLETION_TTS_ID = "__completion__";
const FAILURE_THRESHOLD = 3;

// 第 2 層 (LLM) の fail-open。同じ設問で連続してこの回数弾いたら判定を降ろす
// (spec 2026-07-28 D7)。第 1 層は語彙との完全一致なので誤判定の余地が構造的に
// 無く、fail-open を掛けない。
const MAX_LLM_REJECTIONS = 2;

// 回答の入力経路。判定 (§3.2 / §3.3) は音声にだけ掛ける — テキスト入力は
// 原則 5 の最後の手段で、そこを塞ぐと出口が無くなる。
type AnswerSource = "voice" | "text";

// ✏️ 文字を直す の 1 件分 (spec 2026-08-02 D3)。↻ やり直す と Review の per-Q 修正は
// 再回答経由で新しいターンとして manifest.turns に残るので、ここには積まない。
type AnswerCorrection = NonNullable<AnswerEntry["corrections"]>[number];

// 確認バブル: 回答を受け取った合図 (履歴表示 / SubmitSummary 内リストでも再利用)。
// 「記録しました」の事務的な言い方をやめ、お礼で温かく受け止める。pause + 「いいですか?」は
// 付けない (auto-advance なので確認プロンプトは不要)。答えた内容は直前の user バブルに出ている
// ので、ここで質問文を読み返さず短いお礼にとどめる (TTS でも自動再生される)。
function confirmText(q: Question, value: number | string | null): string {
  if (q.type === "scale") return `✓ ありがとうございます。「${value}」ですね。`;
  return TTS_PHRASES.answerThanks;
}
/**
 * 会話ログを描画するか (spec 2026-08-04 D8)。既定は false — 設問が画面に読めると
 * 「音声だけで成立するか」を被験者テストで測れない (spec §1.2)。
 *
 * 判定を module-top const にしないのは、テストが history.replaceState で search を
 * 差し替えられるようにするため (debugLogger / VadDebugOverlay と同じ流儀)。
 */
function shouldShowConversationLog(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("log") === "1";
}

type SessionBootstrap = { userId: string; sessionId: string; startedAt: string };
type Props = {
  surveyUrl: string;
  micEnabled?: boolean;
  session?: SessionBootstrap; // App が bootstrap 済みの場合に渡す。attach を再呼び出ししない
  displayName?: string; // Preflight 確定の表示名。manifest / session.started に載せる
  outputDeviceId?: string; // Preflight Step 3 確定の出力先。本編 TTS に setSinkId 適用
};

export function ChatScreen({
  surveyUrl,
  micEnabled = false,
  session,
  displayName,
  outputDeviceId,
}: Props) {
  const { survey, answers, setSurvey, setAnswer, setStatus, errorMessage } = useSurveyStore();
  const { messages, append, clear: clearChat } = useChatStore();
  const [phase, setPhase] = useState<Phase>("loading");
  const [activeIdx, setActiveIdx] = useState(0);
  const [micState, setMicState] = useState<MicState>("idle");
  // continuous mode の直近発話 PCM (16 kHz mono)。onUtteranceAudio が積み、
  // onTranscriptCommitted がその発話の transcript を受け取った時点で読み取り +
  // クリアして handleAnswer に引数で渡す (発話 audio と turn の対応は commit 時点で確定)。
  const pendingUtteranceAudioRef = useRef<Float32Array | null>(null);
  /**
   * **今まとめている発話** (2026-08-15)。1 発話が複数の確定文に割れることへの対処。
   *
   * 本番の実測 (session 36a9cde0): 60.2 秒の発話が 179 文字 + 130 文字の 2 つの
   * 確定文に分かれ、2 件目が 1 件目を上書きして**話した内容の 58% が失われた**。
   * サーバが蓄積音声に自動 commit を掛けるので、こちらの commit 回数とは無関係に割れる。
   *
   * `full` に連結して**毎回そこから作り直す**。断片ごとに `handleAnswer` を走らせて
   * 後から継ぎ足す形にしないのは、構造化 LLM が断片単位で整形してしまい
   * 「179 文字の整形結果 + 130 文字の整形結果」が全文の整形結果と一致しないため。
   *
   * `run` は世代トークン。連結して作り直すたびに増やし、**古い run が返ってきたら
   * 何もせずに捨てる** (`isStaleRun`)。これで「1 件目の構造化が遅れて返り、
   * 2 件目の結果を上書きする」競合が構造的に消える。
   */
  const utteranceRef = useRef<{
    id: number;
    full: string;
    run: number;
    audio: Float32Array | null;
    transcriptId: string | null;
    /**
     * **この発話がどの設問に着地したか** (着地前は null)。
     *
     * 1 件目が着地しきってから続きが届くことがある (構造化 LLM が速いと起こる。
     * 実ログの断片間隔 0.97 秒に対し structure の p50 は 1.2 秒なので現実に踏む)。
     * そのとき `activeIdx` は既に次の設問を指しているので、**着地先を覚えておかないと
     * 連結した全文を次の設問の回答として書いてしまう** — 今日の欠陥より悪い
     * (実測で確認した回帰)。
     */
    landing: { questionId: string; transcriptId: string; transcriptAtLanding: string } | null;
  } | null>(null);
  const utteranceSeqRef = useRef(0);
  // 2026-07-09: 読み上げ (useNarration) が useContinuousMode の返り値を必要とするが、
  // continuous 自体はこの function の下で宣言される。循環を避けるため ref で受け渡す。
  const continuousRef = useRef<ReturnType<typeof useContinuousMode> | null>(null);
  const currentTurnIdRef = useRef<string>("");
  const currentTurnQidRef = useRef<string>("");
  const currentTurnStartedAtRef = useRef<string>("");
  // beginTurn (continuous の宣言より前で定義される) から Scribe へ turn 文脈を
  // 伝えるための ref 橋渡し。continuousRef と同じ理由・同じパターン (spec 2026-07-30 §4.2)。
  const setTurnContextRef = useRef<((ctx: { turnIndex: number; turnId: string }) => void) | null>(
    null
  );
  // Task 8 (spec §6.3 SessionEndedPayload.durationMs): session 開始 ISO timestamp。
  // chatStore は sessionId のみ保持し startedAt を持たないため、handleSubmit の
  // session.ended emit で durationMs を計算するために ChatScreen 側で追跡する。
  const sessionStartedAtRef = useRef<string>("");
  // answers.json に載せる userId (spec 2026-08-02)。identityProvider の解決は session
  // bootstrap の IIFE の中だけで、そこから外へ持ち出す入れ物がこれまで無かった。
  const sessionUserIdRef = useRef<string>("");
  // ✏️ の訂正履歴を questionId ごとに積む。surveyStore は最新値しか持たない
  // (setAnswer が value と transcript を同時に上書きする) ので、ハンドラが
  // その場で持っている before / after をここで捕まえておくしかない。
  const correctionsRef = useRef<Record<string, AnswerCorrection[]>>({});
  // 確認フォームに入った時刻と、そのとき音声が取っていた値 (spec 2026-08-04 §6.2)。
  // review.submitted の edited / filled / durationMs を出すのに要る。
  const reviewEnteredAtRef = useRef(0);
  const reviewBaselineRef = useRef<Record<string, string>>({});
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [resumeButtonsExpanded, setResumeButtonsExpanded] = useState(false);
  const [chipExpanded, setChipExpanded] = useState(false);
  // streaming STT (spec §3.12): Scribe の partial_transcript を末尾に薄色ユーザバブルで表示。
  const [partialText, setPartialText] = useState<string>("");
  const forceTextFallback = consecutiveFailures >= FAILURE_THRESHOLD;
  const forceChipFallback = !micEnabled || forceTextFallback;
  // 1 度だけ解決する (App の shouldShowDebugPanel と同じ流儀)。セッション中に
  // URL が変わることは無いので、毎レンダー読み直す必要が無い。
  const [showLog] = useState(shouldShowConversationLog);

  // 会話スレッドの自動スクロール (spec 2026-07-27)。1 問終わるごとに お礼 → 次の質問 が
  // 積まれて新しい質問が画面外に出ていたので、最下部付近にいるときだけ最下部へ追従する。
  // deps はスレッドに中身が増える契機 (spec §3.2): messages = バブル全般、
  // partialText = 発話中の薄色バブル、phase = 処理中インジケータ / Review カード / 続きから確認。
  const thread = useFollowBottom([messages, partialText, phase]);

  // AI の声はこの hook が 1 本で持つ (spec 2026-07-28 §3.5)。未読のキュー・再生位置・
  // 保持 (割り込み判定待ち)・SSML の差し替え・再生状態の表示をまとめてある。
  // 無音の再依頼の制御は continuousRef 越しに渡す (continuous はこの下で宣言されるため)。
  const narration = useNarration({
    pauseSilence: () => continuousRef.current?.pauseSilence(),
    resumeSilence: () => continuousRef.current?.resumeSilence(),
  });
  const appendWithSsml = narration.appendWithSsml;

  // 吹き出しが増えたらキューに積む。読み上げ自体は hook 側で 1 本に直列化されるので、
  // ここで再生ループを回さない (以前は messages が変わるたびに新しいループが起動し、
  // 後から 1 件 append されるだけで直前の未読が読み飛ばされていた)。
  useEffect(() => {
    narration.enqueue(messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Preflight Step 3 で確定した出力デバイスを本編 TTS に適用する (spec §4.1 / D5)。
  // TtsPlayer は sinkId を field 保持して play ごとに適用するため一度で足りる。
  // 適用は fire-and-forget (失敗しても既定出力で再生継続、原則 5)。
  useEffect(() => {
    if (!outputDeviceId) return;
    narration.setSinkId(outputDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputDeviceId]);

  // survey 読み込み + 初期メッセージ
  useEffect(() => {
    let cancelled = false;
    loadSurvey(surveyUrl)
      .then((s) => {
        if (cancelled) return;
        setSurvey(s);
        clearChat();
        // セッション開始 (voice-first: 失敗しても UI を止めない)
        void (async () => {
          const userId = session?.userId ?? (await identityProvider.getUserId());
          const sessionId = session?.sessionId ?? crypto.randomUUID();
          const startedAt = session?.startedAt ?? new Date().toISOString();
          // CRITICAL: clearChat() (この IIFE 直前) が chatStore.sessionId を null に戻すため、
          // App が bootstrap 済み (session prop あり) でも必ず再セットする。同一 sessionId の
          // 再セットは冪等。これを session prop で skip すると sessionId gate の全 emit /
          // turn artifact enqueue / session.ended / summary upload が沈黙する (原則 6 違反)。
          useChatStore.getState().startSession({ sessionId });
          const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
          sessionStartedAtRef.current = startedAt;
          sessionUserIdRef.current = userId;
          await sessionUploader.startSession({
            userId,
            sessionId,
            surveyId: s.id,
            userAgent,
            startedAt,
            ...(displayName ? { displayName } : {}),
          });
          if (!session) {
            // App が Preflight 開始時に attach 済みの場合、attach() は内部 detach で
            // preflight.* を含む buffer をリセットするため再呼び出ししない (prop の有無で分岐)
            sessionEventBuffer.attach({ userId, sessionId, startedAt });
          }
          eventBus.emit(
            makeEvent("session.started", sessionId, {
              userId,
              surveyId: s.id,
              startedAt,
              userAgent,
              viewport: { w: window.innerWidth, h: window.innerHeight },
              micGranted: micEnabled,
              // セッション単位の STT プロバイダ判別の一次キー (spec 2026-07-30 §3.7)
              sttProvider: resolveSttProvider(),
              previousBeaconFailed: readPreviousBeaconFailed(),
              ...(displayName ? { displayName } : {}),
            })
          );
        })();
        const persisted = useSurveyStore.getState().answers;
        const hasAnswers = Object.values(persisted).some((a) => a.value !== null && a.value !== "");
        if (hasAnswers) {
          const doneN = Object.values(persisted).filter(
            (a) => a.value !== null && a.value !== ""
          ).length;
          append({
            kind: "ai-greeting",
            text: `お帰りなさい。前回は ${doneN} 問目までお答えいただいていました。`,
          });
          append({
            kind: "ai-resume-prompt",
            text: TTS_PHRASES.resumePrompt,
          });
          setPhase("resume-prompt");
          return;
        }
        append({ kind: "ai-greeting", text: TTS_PHRASES.greeting });
        const firstUnansweredIdx = findFirstUnansweredIdx(s, persisted);
        setActiveIdx(firstUnansweredIdx);
        if (firstUnansweredIdx < s.questions.length) {
          beginTurn(s.questions[firstUnansweredIdx].id);
          append({
            kind: "ai-question",
            questionId: s.questions[firstUnansweredIdx].id,
            text: questionText(s.questions[firstUnansweredIdx]),
          });
          setPhase("asking");
        } else {
          showSummary();
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setPhase("error");
          setStatus("error", e.message);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUrl]);

  /**
   * 今どの設問に答えているか。確認 phase は擬似 qid を返す。
   *
   * 吹き出しを積む側 (エラー案内・無音の呼びかけ) と読む側 (partial バブル) が
   * 同じ値を使う必要があるので 1 箇所にまとめてある。survey 未ロードでも呼ばれうる
   * (onSilenceEscalation / onError) ため survey は optional に扱う。
   */
  const currentQid = (): string => {
    if (phase === "resume-prompt") return RESUME_PROMPT_QID;
    if (phase === "summary") return SUMMARY_QID;
    return survey?.questions[activeIdx]?.id ?? SUMMARY_QID;
  };

  const showSummary = () => {
    // 後半はフォーム (spec 2026-08-04 D5)。マイクが生きていると、画面を読み上げる声や
    // 独り言が回答として拾われる。stop() は冪等で handleSubmit も同じものを呼ぶ。
    //
    // continuous ではなく ref を使う: showSummary の宣言は useContinuousMode より
    // 前にあり、直接参照すると TDZ になる (beginTurn と同じ回避)。
    continuousRef.current?.stop();
    // 画面が変わったことを、目を離していた人にも伝える 1 回だけの読み上げ (§5.4)。
    // 文言は「声で答えてください」ではなくフォームへの誘導 (U3) — マイクは止めてある。
    appendWithSsml({ kind: "ai-summary" }, TTS_PHRASES.submitConfirm);
    reviewEnteredAtRef.current = Date.now();
    // 前半で音声が取った値を控える。後半で人が直した差分が STT 精度の一次データになる
    // (spec §3.2)。**ここで撮らないと比較の基準が消える** — フォームは store を
    // 直接書き換えるので、送信時には「元が何だったか」が残っていない。
    const { survey: s, answers: current } = useSurveyStore.getState();
    if (s) {
      reviewBaselineRef.current = Object.fromEntries(
        s.questions.map((q) => {
          const v = current[q.id]?.value;
          return [q.id, v === null || v === undefined ? "" : String(v)];
        })
      );
    }
    const sid = useChatStore.getState().sessionId;
    if (sid) {
      const total = s?.questions.length ?? 0;
      const answered = Object.values(reviewBaselineRef.current).filter((v) => v !== "").length;
      eventBus.emit(makeEvent("review.entered", sid, { answered, total }));
    }
    setPhase("summary");
  };

  /**
   * ターンの開始を記録する。質問の吹き出しを積む 5 箇所から呼ぶ (spec 2026-07-30 §4.1)。
   *
   * ここを通らないと turnId / startedAt が「回答が確定した瞬間」に採番され、
   * events.jsonl に turnId が付かず manifest.turns[].startedAt が送信時刻になる。
   * **言い直しを促す経路 (回答が成立しなかったとき) からは呼ばない** — 同じターンの
   * 継続であり、更新すると「考えていた時間」が所要時間から消える。
   *
   * `continuous` (useContinuousMode の戻り値) を直接参照しないのは、その宣言が
   * この関数より後にあり TDZ になりうるため。setTurnContextRef 経由で呼ぶ
   * (hook 内の optsRef と同じ回避、実行時には必ず埋まっている)。
   */
  const beginTurn = (questionId: string) => {
    const turnId = crypto.randomUUID();
    currentTurnIdRef.current = turnId;
    currentTurnQidRef.current = questionId;
    currentTurnStartedAtRef.current = new Date().toISOString();
    setTurnContextRef.current?.({ turnIndex: useChatStore.getState().turnIndex, turnId });
  };

  const advanceTo = (nextIdx: number, s: Survey) => {
    if (nextIdx >= s.questions.length) {
      showSummary();
      return;
    }
    setActiveIdx(nextIdx);
    setChipExpanded(false);
    beginTurn(s.questions[nextIdx].id);
    append({
      kind: "ai-question",
      questionId: s.questions[nextIdx].id,
      text: questionText(s.questions[nextIdx]),
    });
    setPhase("asking");
  };

  /**
   * 確定回答の一覧 (spec 2026-08-02 D1)。survey 定義の設問順に全設問を並べ、
   * 未回答は value: null で残す — Record にすると未回答の設問がキーごと落ちて
   * 「5 問中 3 問」を表現できない。
   *
   * answers を store から読み直すのが要点 (spec §5.2)。ChatScreen の分割代入は
   * 同一 tick 内では 1 つ前の値のままで、✏️ 直後に組み立てると訂正前の値が載る。
   * setAnswer は set({answers}) を同期で撃つので getState() は即座に新しい。
   */
  const buildAnswersArtifact = (sessionId: string, feedback?: string): AnswersArtifact | null => {
    const { survey: s, answers: current } = useSurveyStore.getState();
    if (!s) return null;
    return {
      schemaVersion: 1,
      sessionId,
      userId: sessionUserIdRef.current,
      surveyId: s.id,
      generatedAt: new Date().toISOString(),
      // 空文字はキーごと省略する (spec 2026-08-04 D7)。「書かなかった」と
      // 「空欄のまま送った」を区別できなくしないため。
      ...(feedback ? { feedback: { text: feedback, submittedAt: new Date().toISOString() } } : {}),
      answers: s.questions.map((q) => {
        const a = current[q.id];
        const corrections = correctionsRef.current[q.id];
        return {
          questionId: q.id,
          // 焼き込むのは設問の生テキスト。questionText() が scale に足す
          // 「1 (…) 〜 5 (…) でお答えください。」は読み上げの言い回しであって
          // 「何を聞かれたか」ではなく、admin も survey 定義の q.text を出している。
          questionText: q.text,
          value: a?.value ?? null,
          transcript: a?.transcript ?? null,
          // 未回答は 0。答えていないのに時刻を捏造しない。
          updatedAt: a?.updatedAt ?? 0,
          // 複製して載せる。参照のまま渡すと、次の ✏️ の push が
          // **enqueue 済みの artifact を後から書き換える** (同じキーへの全上書きなので
          // 通常は次の PUT が自己修復するが、直列キューが詰まっている間は差が見える)。
          ...(corrections && corrections.length > 0 ? { corrections: [...corrections] } : {}),
        };
      }),
    };
  };

  /**
   * 確定回答を R2 へ送る (spec 2026-08-02 D2)。回答の確定 / ✏️ 訂正 / 送信の 3 契機から
   * 同じキーへ冪等に全上書きする。await しない (原則 5)。
   *
   * try で包むのは、uploader の buildRequest が dispatch の try の外で呼ばれるため
   * (spec §5.1)。保存は成果物であって体験ではないので、ここが throw しても
   * 訂正の反映や送信完了を道連れにしない。
   */
  const saveAnswers = (sessionId: string, feedback?: string) => {
    try {
      const artifact = buildAnswersArtifact(sessionId, feedback);
      if (!artifact) return;
      sessionUploader.enqueueAnswers({ sessionId, artifact });
    } catch {
      // 保存の失敗で画面を止めない (原則 5)
    }
  };

  // 読み上げを保持しているとき、聞き取った内容が「本当の回答」かを端末側で即断する
  // (spec 2026-07-28 §3.4)。not-answer なら読み上げを続きから再開して発話ごと捨てる。
  // 送信確認と続きから確認では期待する答えが読み上げ文に引用されているので、
  // テキストだけでは AI 由来かユーザー由来かを判別できない (D12)。判定は空だけに絞る。
  const judgeHeld = (text: string) =>
    judgeInterruption(text, {
      narratingText: narration.currentText(),
      quotedAnswers: phase === "summary" || phase === "resume-prompt",
    });

  // 判定待ちだったなら、聞き直しを出さずに読み上げを続きから再開する (spec D4)。
  // 咳ひとつで聞き直しを食らうのは体験として悪く、その聞き直しの読み上げ自体が
  // また誤爆の種になる。戻り値は「再開したか」。
  const resumeIfHeld = (reason: Parameters<typeof narration.resume>[0]): boolean => {
    if (!narration.isHeld()) return false;
    narration.resume(reason);
    return true;
  };

  // 同じ設問で連続して弾いた回数。段階的な聞き直し (§3.4) と LLM の fail-open (§3.6)
  // が読む。設問をまたいで持ち越すと、前の設問で弾かれた人が次の設問の 1 回目から
  // いきなり質問文を読み直されるので、qid と一緒に持って変わったら作り直す。
  const rejectionsRef = useRef({ qid: "", total: 0, llm: 0 });

  const rejectionsFor = (qid: string) => {
    if (rejectionsRef.current.qid !== qid) {
      rejectionsRef.current = { qid, total: 0, llm: 0 };
    }
    return rejectionsRef.current;
  };

  /**
   * 回答として成立していないと判定した発話の後始末 (spec 2026-07-28 §3.4 / §5)。
   *
   * 読み上げを保持したまま来た発話は、聞き直しを出さずに読み上げを再開して捨てる。
   * 出すと、その聞き直しの読み上げ自体がまた割り込み判定の誤爆の種になる。吹き出しも
   * 残さない — 保持中の発話は「回答の試み」ではなく物音である可能性が高く、咳のたびに
   * 吹き出しが残ると会話が読めなくなる (D5 の例外)。
   */
  const rejectAnswer = (
    q: Question,
    transcriptId: string,
    wasHeld: boolean,
    layer: "local" | "llm",
    reason: AnswerRejectReason | Extract<AnswerVerdictLabel, "no-content" | "loopback">
  ) => {
    if (wasHeld) {
      useChatStore.getState().removeMessage(transcriptId);
      narration.resume(
        layer === "llm" ? "llm-loopback" : reason === "empty" ? "empty" : "backchannel"
      );
      setPhase("asking");
      return;
    }
    const r = rejectionsFor(q.id);
    r.total += 1;
    if (layer === "llm") r.llm += 1;
    // 保持中に弾いた分はここへ来ない (上で return 済)。あちらは bargein.resumed が
    // 同じ事実を持っているので二重に数えない。
    const sid = useChatStore.getState().sessionId;
    if (sid) {
      eventBus.emit(
        makeEvent("answer.rejected", sid, {
          layer,
          reason,
          questionId: q.id,
          attempt: r.total,
          llmRejections: r.llm,
        })
      );
    }
    if (r.total === 1) {
      append({ kind: "ai-confirm", questionId: q.id, text: TTS_PHRASES.answerTooThin });
    } else {
      // 2 回目以降は設問を言い直す (D4)。1 つの吹き出しに連結しないのは、
      // collectPhrases が TTS_PHRASES と設問文を別々に数え上げるため (§3.4)。
      append({ kind: "ai-confirm", questionId: q.id, text: TTS_PHRASES.answerReask });
      append({ kind: "ai-question", questionId: q.id, text: questionText(q) });
    }
    // 既存の失敗カウンタに載せる (D8)。3 回でテキスト入力が開く。
    setConsecutiveFailures((c) => c + 1);
    setPhase("asking");
  };

  // resume-prompt phase の intent ハンドラ (voice-first 一次動線)
  const handleIntentResume = async (transcript: string) => {
    const transcriptId = append({
      kind: "user-transcript",
      questionId: RESUME_PROMPT_QID,
      text: transcript,
    });
    setPhase("processing");
    try {
      const result = await structure({
        questionType: "intent",
        questionText: "続きから始めますか? それとも最初から?",
        transcript,
        expected: ["resume", "restart"] as IntentLabel[],
      });
      setConsecutiveFailures(0);
      if (result.intent === "resume") {
        narration.abandon();
        handleResume();
      } else if (result.intent === "restart") {
        narration.abandon();
        handleStartOver();
      } else {
        // 意図を読み取れなかった。読み上げ中の割り込みだったなら聞き直しを出さず、
        // 読み上げを続きから再開する (spec §3.6(3))。
        if (resumeIfHeld("llm-no-value")) {
          useChatStore.getState().removeMessage(transcriptId);
          setPhase("resume-prompt");
          return;
        }
        // 選択肢と依頼の間に 500ms pause。inline SSML なので UI 表示は不変。
        appendWithSsml(
          { kind: "ai-confirm", questionId: RESUME_PROMPT_QID },
          TTS_PHRASES.resumeChoice
        );
        setPhase("resume-prompt");
      }
    } catch (e) {
      setStatus("error", (e as Error).message);
      if (resumeIfHeld("error")) {
        useChatStore.getState().removeMessage(transcriptId);
        setPhase("resume-prompt");
        return;
      }
      setConsecutiveFailures((c) => c + 1);
      // 説明 → 500ms pause → 依頼 (聞き取れなかった時の共通再依頼)
      appendWithSsml(
        { kind: "ai-confirm", questionId: RESUME_PROMPT_QID },
        TTS_PHRASES.retryGeneric
      );
      setPhase("resume-prompt");
    }
  };

  // utteranceAudio: onTranscriptCommitted が commit 時点で pending から取り出した
  // 「この transcript と同じ発話」の PCM。テキスト/チップ入力経路では undefined。
  /**
   * 世代トークンが古いか (2026-08-15)。**古い run は state を 1 つも触らずに捨てる** —
   * 新しい run が既に phase を所有しているので、ここで setPhase などを撃つと剥がしてしまう。
   */
  /**
   * **着地した回答を、伸びた全文で正す** (2026-08-15)。
   *
   * `handleAnswer` をやり直すと `activeIdx` が既に次の設問を指しているので全文が
   * そちらの回答になってしまう (今日の欠陥より悪い。実測で確認した)。
   * **着地先の設問だけを直し、進行も画面の遷移も動かさない** — ユーザーは次の設問に
   * 答えている最中なので、そちらを邪魔しない。
   *
   * 触らないもの: `setPhase` / `advanceTo` / `beginTurn` / `narration` / `autoadvance` /
   * 発話 WAV (着地時に 1 本保存済み) / 聞き直しのカウンタ。
   *
   * **回答を悪くしない**のが原則 — 構造化が値を返さなければ着地時の回答をそのまま残す。
   */
  const repairLanding = async (
    landing: { questionId: string; transcriptId: string; transcriptAtLanding: string },
    full: string
  ) => {
    if (!survey) return;
    // 送信後は触らない (確認フォームに出ている値を後から書き換えない)
    if (phaseRef.current === "summary" || phaseRef.current === "submitted") return;
    const q = survey.questions.find((x) => x.id === landing.questionId);
    if (!q) return;
    // 人が ✏️ / ↻ / Review で直していたら、その訂正を巻き戻さない
    const current = useSurveyStore.getState().answers[landing.questionId];
    if (!current || current.transcript !== landing.transcriptAtLanding) return;
    try {
      const input =
        q.type === "scale"
          ? {
              questionType: "scale" as const,
              questionText: q.text,
              transcript: full,
              min: q.min,
              max: q.max,
            }
          : q.type === "short"
            ? { questionType: "short" as const, questionText: q.text, transcript: full }
            : { questionType: "long" as const, questionText: q.text, transcript: full };
      const result = await structure(input);
      if (result.value === null || result.value === "") return; // 悪くしない
      setAnswer(landing.questionId, result.value, full);
      useChatStore.getState().updateText(landing.transcriptId, full);
      const sid = useChatStore.getState().sessionId;
      if (sid) saveAnswers(sid);
    } catch {
      // 直せなくても着地時の回答は残る (fail-safe)
    }
  };

  const isStaleRun = (token?: { id: number; run: number }) => {
    if (!token) return false;
    const u = utteranceRef.current;
    return !u || u.id !== token.id || u.run !== token.run;
  };

  const handleAnswer = async (
    raw: string | number,
    source: AnswerSource,
    utteranceAudio?: Float32Array,
    // 発話が割れて作り直されたときの世代 (音声経路だけが渡す)
    token?: { id: number; run: number }
  ) => {
    if (!survey) return;
    setStatus("idle", null);
    // これから回答が確定するので、認識中の途中経過を出しておく意味はもう無い。
    // 音声経路は確定文の到着 (onTranscriptCommitted) で消えるが、**テキストと
    // チップにはその契機が無い**。確定文は WS のアイドル切断で届かないことがあり
    // (ensureScribeConnected が古い client を閉じる)、そのとき宙に浮いた途中経過は
    // 描画位置がスレッド末尾・questionId が現在の設問なので、**設問が進むたびに
    // 新しい設問の下へ付いてくる**。音声が通らなかったからテキストに切り替えるので、
    // まさにこの経路にクリアが要る。
    setPartialText("");

    // 答えるべき問いがまだ一度も鳴っていない = ユーザーはそれを聞いていない。
    // 毎ターン お礼 と 次の設問 が同時に積まれ、読み上げは 1 件ずつ進むので、
    // お礼を鳴らしている約 2 秒 (greeting は実測 14.5 秒) は「次の問いがキューで
    // 待っている」窓になる。そこで言い足した言葉を受理すると、**聞いてもいない問いの
    // 回答として記録され、しかもその問いは読まれないまま次へ進む** (チケット H1)。
    // R2 には設問と噛み合わない回答が残り、後から見ても正常な回答と区別がつかない。
    //
    // **対象は設問だけ。** 「続きから確認」と送信確認にも同じ窓があり症状も同型
    // (聞いていない確認に答えたことになる) だが、そちらはチケット
    // `2026-08-02-confirmation-not-yet-read.md` で別に扱う — 確認 phase は
    // 「はい/いいえ」の 1 語で答えるため誤って弾いたときの影響が設問と違い、
    // 実機で起きているかも未確認なので、まとめて広げない。
    //
    // 読み上げ**中**の割り込み (barge-in) は従来どおり受理する。塞ぐのは
    // 「一度も鳴っていない」場合だけで、その判定は narration 側に閉じてある。
    // テキスト入力には掛けない — 画面で読んで打っているので、聞いていなくてよい。
    if (source === "voice" && narration.hasQueued("ai-question")) {
      resumeIfHeld("not-yet-asked");
      return;
    }

    if (phase === "resume-prompt") {
      await handleIntentResume(String(raw));
      return;
    }

    if (activeIdx >= survey.questions.length) return;
    const q = survey.questions[activeIdx];
    const transcript = String(raw);
    const transcriptId = append({ kind: "user-transcript", questionId: q.id, text: transcript });
    // 台帳に覚えさせる。発話が伸びて作り直すとき、この吹き出しを消してから積み直す
    // (消さないと画面に断片が 2 つ並ぶ、2026-08-15)
    if (token && utteranceRef.current?.id === token.id) {
      utteranceRef.current.transcriptId = transcriptId;
    }
    // 読み上げを保持したまま来たかを **await の前に** 覚えておく。構造化 LLM の往復中に
    // 6 秒のタイムアウトで読み上げが再開すると `isHeld()` は false に戻るので、
    // 後で読むと**遅い応答のときだけ事後照合が skip される** — LLM が遅いときこそ
    // 回り込みを取り逃がしたくないのに、そこだけ網が外れる。
    const wasHeld = narration.isHeld();

    // 第 1 層 (spec §3.2): 端末側だけで即断する。API 往復ゼロなので processing に
    // 入る前に片付ける。テキスト/チップ入力には掛けない (D6)。
    if (source === "voice") {
      const judged = judgeAnswer(transcript);
      if (judged.verdict === "not-answer") {
        rejectAnswer(q, transcriptId, wasHeld, "local", judged.reason ?? "empty");
        return;
      }
    }

    setPhase("processing");

    try {
      let value: number | string | null;
      let verdict: StructureResult["verdict"] = null;
      if (q.type === "scale" && typeof raw === "number") {
        value = raw;
      } else {
        const input =
          q.type === "scale"
            ? {
                questionType: "scale" as const,
                questionText: q.text,
                transcript,
                min: q.min,
                max: q.max,
              }
            : q.type === "short"
              ? { questionType: "short" as const, questionText: q.text, transcript }
              : { questionType: "long" as const, questionText: q.text, transcript };
        const result = await structure(input);
        // **await の間に発話が伸びていたら、この run はもう用済み。** 新しい run が
        // 全文でやり直しているので、ここで先へ進むと古い断片で上書きしてしまう
        // (実ログでは 179 文字が 130 文字に上書きされ 58% が消えた)。
        // **素の return** — 新しい run が phase を所有しているので剥がさない。
        if (isStaleRun(token)) return;
        value = result.value;
        verdict = result.verdict;
      }

      // 第 2 層 (spec §3.3)。fail-open は「同じ設問で LLM が連続 2 回」で降ろす (D7) —
      // 方言や STT の癖で永久に弾かれる人を作らないため。第 1 層はここを通らない。
      if (
        source === "voice" &&
        (verdict === "no-content" || verdict === "loopback") &&
        rejectionsFor(q.id).llm < MAX_LLM_REJECTIONS
      ) {
        rejectAnswer(q, transcriptId, wasHeld, "llm", verdict);
        return;
      }
      if (value === null || value === "") {
        // 読み上げ中の割り込みだったなら聞き直しを出さず、続きから再開する (spec D4)。
        if (resumeIfHeld("llm-no-value")) {
          useChatStore.getState().removeMessage(transcriptId);
          setPhase("asking");
          return;
        }
        setConsecutiveFailures((c) => c + 1);
        // 説明 → 500ms pause → 依頼
        const reAskSsml =
          q.type === "scale"
            ? buildPauseAck(
                `すみません、「${transcript}」から ${q.min}〜${q.max} の数字を聞き取れませんでした`,
                500,
                "もう一度、数字でお聞かせいただけますか。"
              )
            : buildPauseAck(
                `すみません、「${transcript}」をうまく聞き取れませんでした`,
                500,
                "もう一度お聞かせいただけますか。"
              );
        appendWithSsml({ kind: "ai-confirm", questionId: q.id }, reAskSsml);
        setPhase("asking");
        return;
      }
      // 事後照合 (spec §3.6(3))。long / short の prompt には「値が取れなければ null」の
      // 指示が無いので、回り込んだ読み上げ文は逐語整形されてそのまま返る。同じ判定を
      // LLM の出力にも当てると、追加の往復ゼロでもう一枚の網になる。
      if (wasHeld && judgeHeld(String(value)).verdict === "not-answer") {
        useChatStore.getState().removeMessage(transcriptId);
        // タイムアウトで既に再開済みなら resume は no-op。読み上げは続いているので
        // それで正しい (捨てるべきは発話の方)。
        narration.resume("llm-loopback");
        setPhase("asking");
        return;
      }
      // ここまで来たら本物の割り込み。読み上げは捨てて回答として処理する。
      narration.abandon();
      setAnswer(q.id, value, transcript);
      // **着地先を台帳に覚えさせる** (2026-08-15)。この後で発話の続きが届いたとき、
      // `activeIdx` は次の設問を指しているので、ここを覚えていないと全文を
      // 次の設問の回答として書いてしまう (実測で確認した回帰)。
      // `sid` ガードの外に置くのが要点 — session が無い経路でも着地はしている。
      if (token && utteranceRef.current?.id === token.id) {
        utteranceRef.current.landing = {
          questionId: q.id,
          transcriptId,
          transcriptAtLanding: transcript,
        };
      }
      // この設問は答え直された。↻ やり直す と Review の per-Q 修正もここを通るので、
      // 以前の ✏️ 訂正は「今の回答」への訂正ではなくなる。捨てないと answers.json が
      // 「今の値」と「もう存在しない値への訂正」を並べて持ち、admin がそれを
      // 「この回答はこう誤認識され、こう直された」として描く — spec §5.7 が
      // 「両者の差分がそのまま STT 精度の一次データ」と書いた当のデータに、
      // どちらの値とも対応しないペアが 1 件混ざる。
      delete correctionsRef.current[q.id];
      setConsecutiveFailures(0);
      // 回答が 1 件通ったので連続は途切れた。LLM の fail-open もここで戻す (§3.6)。
      rejectionsRef.current = { qid: "", total: 0, llm: 0 };
      // LLM + metrics artifact enqueue (voice-first: fire-and-forget)
      const { sessionId: sid, turnIndex: ti } = useChatStore.getState();
      if (sid) {
        const turnId = currentTurnIdRef.current || crypto.randomUUID();
        // 確定回答の一覧を撮り直す (spec 2026-08-02 D2)。送信時だけにしないのは、
        // 実測で 22 セッション中 13 件が送信に至っておらず、その回答が丸ごと
        // 失われているため (途中離脱した被験者が何と答えていたかは分析に要る)。
        //
        // **発話 WAV より先に積む。** SessionUploader は 1 本の直列キューで、
        // long 回答の WAV は 10 秒で約 320 KB になる。後ろに積むとその送信が
        // 終わるまで answers の PUT が始まらず、離脱がその窓に入ると
        // D2 が拾おうとした「最後の 1 問」がちょうど落ちる。
        saveAnswers(sid);
        // 発話 audio artifact enqueue (voice-first: fire-and-forget)。utteranceAudio は
        // この transcript を生んだ発話の PCM (commit 時点で対応確定済)。turnId 確定後に
        // WAV 化して R2 に保存する。テキスト/チップ入力経路では undefined なので
        // 何も enqueue しない。
        if (utteranceAudio) {
          sessionUploader.enqueueAudio({
            sessionId: sid,
            turnIndex: ti,
            turnId,
            blob: encodeWavPcm16(utteranceAudio),
          });
        }
        sessionUploader.enqueueLlm({
          sessionId: sid,
          turnIndex: ti,
          schemaVersion: 1,
          turnId,
          provider: "openai",
          model: "gpt-4o-mini",
          recordedAt: new Date().toISOString(),
          request: { messages: [], schemaName: q.type },
          response: { structured: { value }, raw: {} },
        });
        // A4-2: 骨組みだけを書いていたので `turns/*/metrics.json` が全件空だった
        // (R2 上のサイズが 107 バイトで固定)。schema は stt / llm / tts を持っている。
        // 窓の起点は**このターンが始まった時刻** — 前のターンの重い処理を借りると、
        // 答えるのが速かったターンほど悪く見える。
        const turnStartedMs = Date.parse(currentTurnStartedAtRef.current || "");
        sessionUploader.enqueueMetrics({
          sessionId: sid,
          turnIndex: ti,
          schemaVersion: 1,
          turnId,
          recordedAt: new Date().toISOString(),
          ...buildTurnMetrics(
            getMetrics(),
            sid,
            // 開始時刻が読めない回は窓を切らない (0 = 全件から最後のものを採る)。
            // 混ざるほうが、その turn の計測が丸ごと空になるより読める。
            Number.isNaN(turnStartedMs) ? 0 : turnStartedMs
          ),
        });
        sessionUploader.appendTurnToManifest({
          sessionId: sid,
          turnIndex: ti,
          turnId,
          questionId: currentTurnQidRef.current || q.id,
          startedAt: currentTurnStartedAtRef.current || new Date().toISOString(),
          completedAt: new Date().toISOString(),
        });
        useChatStore.getState().nextTurn();
        currentTurnIdRef.current = "";
        currentTurnQidRef.current = "";
        currentTurnStartedAtRef.current = "";
      }
      // 2026-07-11 UX iteration: continuous mode では「いいですか?」の explicit
      // confirmation が冗長 (実機テストで user feedback)。「✓ ありがとうございます。「3」ですね。」の
      // 短い feedback だけ出し即 advance する (auto-advance)。
      // 訂正は user-transcript の ↻ やり直す / ✏️ 文字を直す / 過去 turn 遡及編集で
      // 対応する (correction UX、仕様 §6.1 / §4.2)。
      append({
        kind: "ai-confirm",
        questionId: q.id,
        text: confirmText(q, value),
      });
      // Task 8 (spec §6.2 #18): auto-advance のみ emit (intent==="yes" 由来の
      // advance はここを通らないので対象外)。sessionId が無くても advance 自体は
      // voice-first のため必ず実行する。
      if (sid) {
        eventBus.emit(
          makeEvent("autoadvance.fired", sid, {
            fromTurnIndex: activeIdx,
            toTurnIndex: activeIdx + 1,
          })
        );
      }
      advanceTo(activeIdx + 1, survey);
    } catch (e) {
      // 古い run のエラーは黙って捨てる。新しい run が全文で処理中なので、ここで
      // 聞き直しを出すと「まだ話している最中に聞き直される」ことになる。
      if (isStaleRun(token)) return;
      setStatus("error", (e as Error).message);
      // 保持中でも、ここへ来た発話は**即断ルールを通過した = 本物の回答らしい**もの。
      // API の一時障害で黙って消すと、ユーザーの言葉が画面に出て消えたきりになり、
      // 失敗回数も増えないのでテキスト入力への案内 (原則 5 の fallback) にも到達しない。
      // 読み上げは再開するが、吹き出しは残して従来どおり聞き直しを出す。
      resumeIfHeld("error");
      setConsecutiveFailures((c) => c + 1);
      // 説明 → 500ms pause → 依頼 (聞き取れなかった時の共通再依頼)
      appendWithSsml({ kind: "ai-confirm", questionId: q.id }, TTS_PHRASES.retryGeneric);
      setPhase("asking");
    }
  };

  // continuous mode (spec §3) + Bug A fix (2026-07-10, Round 2): phase-aware keyterms。
  // Scribe v2 Realtime WSS は connect 時 (query parameter) に keyterms を固定するので、
  // phase 遷移で必要な keyterms が変わる (scale Q の数字 → intent の「はい/いいえ」等) 場合
  // useContinuousMode が useEffect で自動 reconnect する。ChatScreen 側は「今の phase で
  // 欲しい keyterms」を declare するだけ。asking phase のみ現 Q の scale 数字 keyterms を
  // 渡し、resume-prompt / summary では [] を渡す
  // (数字 bias が「はい」「続きから」等の intent 発話を誤変換するのを避ける)。
  // useMemo で React identity を安定化 (毎 render 新 array を返さないため、hook 側の
  // 値比較 useEffect が識別しやすく race も減る)。
  const keytermsForCurrentQ = useMemo(() => {
    if (phase !== "asking") return [];
    if (!survey || activeIdx >= survey.questions.length) return [];
    return buildKeytermsForQuestion(survey.questions[activeIdx]);
  }, [phase, survey, activeIdx]);
  // 質問タイプで VAD の redemption (発話終了判定) を切り替える
  // (keytermsForCurrentQ と同じ phase-aware useMemo パターン)。
  const redemptionMsForCurrentQ = useMemo(() => {
    if (phase !== "asking") return redemptionMsForQuestionType(null);
    if (!survey || activeIdx >= survey.questions.length) return redemptionMsForQuestionType(null);
    return redemptionMsForQuestionType(survey.questions[activeIdx].type);
  }, [phase, survey, activeIdx]);
  // continuous mode の統合 hook (spec §3)。
  // enabled=false (マイク拒否) のときは start() 呼んでも noop で返るので、
  // テキスト/チップ fallback だけで完走できる。
  const continuous = useContinuousMode({
    enabled: micEnabled,
    keytermsForCurrentQ,
    redemptionMs: redemptionMsForCurrentQ,
    onTranscriptCommitted: (text, meta) => {
      setPartialText("");
      // **1 発話が複数の確定文に割れる** (2026-08-15 実測)。`continuation` は
      // 「直前の確定文と同じ発話の続き」を意味する。台帳が無いのに継続を名乗る場合は
      // 新しい発話として扱う — OpenAI 側は `failed` も終端に数えるので、失敗した
      // item の次の確定文が継続を名乗るが、こちらは 1 度も受け取っていない。
      const cont = meta?.continuation === true && utteranceRef.current !== null;

      if (!cont) {
        // 読み上げを保持しているなら、まず端末側の即断ルールに掛ける。明らかに回答でない
        // (空 / 読み上げの回り込み / 相槌) なら、吹き出しも出さずに読み上げを再開する
        // (spec §3.6(2))。API 往復はゼロなので、咳ひとつで無音が伸びることがない。
        //
        // **この即断は 1 件目にだけ掛ける。** 継続は「既に本物の割り込みと認めた発話」の
        // 一部で、しかも連結は文字列を長くするだけ — judgeInterruption の 3 ルール
        // (空 / 回り込み / 相槌) はどれも長くなるほど発火しにくいので、
        // 「1 件目が通ったのに連結後に落ちる」は構造的に起こらない。
        // 全文に対する事後照合 (下の wasHeld && judgeHeld) は従来どおり残る。
        if (narration.isHeld()) {
          const { verdict, reason } = judgeHeld(text);
          if (verdict === "not-answer") {
            narration.resume(reason ?? "empty");
            pendingUtteranceAudioRef.current = null;
            utteranceRef.current = null;
            return;
          }
        }
        // 発話 audio と transcript の対応をここ (commit 時点) で確定する: pending を
        // 読み取り + クリアして台帳が持つ。handleAnswer 内の構造化 await 中に次発話が
        // commit されても、各発話は自分の PCM だけを受け取る。intent phase
        // (resume-prompt / summary 等) や構造化失敗で turn にならなかった発話の audio は
        // handleAnswer 側で enqueue されず破棄される。
        const audio = pendingUtteranceAudioRef.current;
        pendingUtteranceAudioRef.current = null;
        utteranceRef.current = {
          id: ++utteranceSeqRef.current,
          full: text,
          run: 0,
          audio,
          transcriptId: null,
          landing: null,
        };
      } else {
        // **連結子は空文字。** ElevenLabs の確定文は差分で、実ログでは
        // 179 + 130 = 309 文字が「話した文字数」と一致していた (重複なし)。
        // 「…とか、目標」+「とか目的みたいな…」は空文字で繋いで初めて日本語になる。
        utteranceRef.current!.full += text;
      }

      const u = utteranceRef.current!;

      // **着地した後に続きが届いた場合** (構造化が速いと起こる)。`handleAnswer` を
      // やり直すと `activeIdx` が次の設問を指しているため全文がそちらの回答になる —
      // 今日の欠陥より悪い。**着地先の設問だけを直し、画面も進行も動かさない。**
      if (u.landing) {
        void repairLanding(u.landing, u.full);
        return;
      }

      // 前の run が出した吹き出しを消してから作り直す (画面に断片が 2 つ並ばない)
      if (u.transcriptId) {
        useChatStore.getState().removeMessage(u.transcriptId);
        u.transcriptId = null;
      }
      const run = ++u.run;
      void handleAnswer(u.full, "voice", u.audio ?? undefined, { id: u.id, run });
    },
    onPartial: (text) => setPartialText(text),
    // 発話 audio の R2 保存: VAD 切り出し済み PCM を pending に積む。読み取り +
    // クリアは onTranscriptCommitted が commit 単位で行う (voice-first: fire-and-forget)。
    onUtteranceAudio: (audio) => {
      pendingUtteranceAudioRef.current = audio;
    },
    onSilenceEscalation: (level) => {
      // spec §3.5.1: 無音時 escalation (10s x 3 段)
      const msgs = [TTS_PHRASES.silence0, TTS_PHRASES.silence1, TTS_PHRASES.silence2];
      const qid = currentQid();
      append({
        kind: "ai-confirm",
        questionId: qid,
        text: msgs[level] ?? msgs[msgs.length - 1],
      });
      if (level === 1) setConsecutiveFailures((c) => c + 1);
    },
    onError: (e) => {
      // この発話はもう確定文にならない。認識中の途中経過 (live captions) を消せる契機は
      // 確定文の到着だけなので、**エラーを伝えるこの時点で消す**。消さないと聞き直しの
      // 案内の下に薄い吹き出しが残り、次に回答するまで居座る (#56 と同じ見え方)。
      // 早期 return する分岐 (判定待ちの再開) より前に置くのが要点 — その経路も
      // 「この発話は回答にならなかった」という点では同じ。
      setPartialText("");
      const qid = currentQid();
      // 判定待ちの発話が短すぎた / STT が落ちた。聞き直しを出すとその読み上げが
      // また誤爆の種になるので、黙って読み上げを再開する (spec §3.6(4))。
      // 条件を isHeld() 限定にするのが要点 — 「読み上げ中でない」と書くと、
      // 吹き出しを積んだ直後 (読み上げ中) に来る通常のエラーで案内が出なくなる。
      if (e.kind !== "mic-lost" && narration.isHeld()) {
        narration.resume(e.kind === "short-utterance" ? "short-utterance" : "error");
        return;
      }
      if (e.kind === "short-utterance") {
        // spec R3.3.2: < 500ms は STT を skip して recovery
        append({
          kind: "ai-confirm",
          questionId: qid,
          text: TTS_PHRASES.errorShortUtterance,
        });
      } else if (e.kind === "stt-ws-failed" || e.kind === "stt-no-final") {
        // spec §3.9: streaming Scribe 接続失敗 / 送ったのに確定文が返らなかった (A2)。
        //
        // **同じ文言を使う** — ユーザーから見ればどちらも「話したのに聞き取られなかった」で、
        // 取るべき行動 (もう一度話す) も同じ。文言を分けないので**事前生成音声の
        // 作り直しも要らない** (キーは文言から決まる)。計測では
        // `stt-ws-failed` / `stt-no-final` として分かれている。
        append({
          kind: "ai-confirm",
          questionId: qid,
          text: TTS_PHRASES.errorSttFailed,
        });
      } else if (e.kind === "mic-lost") {
        // spec §3.11: mic 切断
        append({
          kind: "ai-confirm",
          questionId: qid,
          text: TTS_PHRASES.errorMicLost,
        });
        setMicState("error");
      }
    },
    ttsPlayer: {
      isPlaying: () => narration.isPlaying(),
      currentMessageId: () => narration.currentMessageId(),
      // 「止めてくれ」の意味を、破棄ではなく一時停止に変えてある (spec 2026-07-28 D2)。
      // 聞き取った内容が本当の回答でなければ、読み上げは続きから再開する。
      stop: () => narration.hold(),
      // 話し終えた時点で保持のタイムアウトを測り直す (spec 2026-08-04 D5)。
      extendHold: () => narration.extendHold(),
    },
  });
  // 2026-07-09: 読み上げ (useNarration) から continuous.pauseSilence /
  // resumeSilence を呼ぶために ref を橋渡し。継続 render でも最新の hook 返り値を保持。
  continuousRef.current = continuous;
  // beginTurn は宣言順で continuous より前にあるため、直接参照すると TDZ になりうる。
  // hook 内の optsRef と同じ回避 (実行時には必ず埋まっている、spec 2026-07-30 §4.2)。
  setTurnContextRef.current = continuous.setTurnContext;

  // 処理中の合図音 (spec 2026-07-27 §3.3)。回答を受け取ってから次の質問が読み上げられる
  // までの無音に、低く短い音を 850ms ごとに置く (画面を見ていない人にも「働いている」を
  // 届ける)。マイクは鳴らす間だけ止める: 合図音がスピーカーからマイクへ回り込んでも
  // Scribe に一切届かないので、前回のターン合図音で必要だったループバック・ガードが要らない。
  // continuous.pause() を使うのは、track を止めるだけでなく vadState も idle に落ちるため
  // (処理中なのに MicButton が発話待ち表示のままになるのを防ぐ)。silence timer も一緒に
  // 止まるが、処理中に無音を責める必要はないので望ましい。
  useProcessingCue({
    // 判定待ちのタイムアウト (spec 2026-07-28 D6) で読み上げが再開しても処理中の状態は
    // 続いたままなので、読み上げを見ないと AI の声の上に 850ms ごとの合図音が重なる。
    // これは 2026-07-25 に合図音を全廃した理由そのもの (spec 2026-07-28 §3.7)。
    cueActive: phase === "processing" && narration.state !== "speaking",
    micActive: phase === "processing",
    suspendMic: continuous.pause,
    resumeMic: () => {
      // タブを離れたまま処理が終わることがある (fetch は hidden でも完走する)。ここで
      // 素直に戻すと、非表示のままマイク track が有効化され VAD と無音エスカレーションまで
      // 動き出す (spec 2026-07-07 R3.1.4 / R3.8.4「タブ非表示中は pause」に反する)。
      // 離席中に「聞こえていますでしょうか」が読み上げられ、聞いていない声が次の回答として
      // 記録されうる。戻すのは下の onVisible に任せる (そのとき phase は processing を
      // 抜けているのでガードには掛からない) — §3.4 のガードと合わせて両方向を閉じる。
      if (document.visibilityState === "hidden") return;
      continuous.resume();
    },
  });

  // 処理中かを visibility ハンドラから読むための ref。useVisibilityPause に渡す
  // コールバックは毎 render 変わるため、phase を直接 capture すると listener の
  // 貼り直しが増える (既存の rerender 追随テストが守っている挙動は保ったまま値だけ最新にする)。
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;
  // 判定待ちかを visibility ハンドラから読むための ref (phaseRef と同じ理由)。
  const narrationHeldRef = useRef(false);
  narrationHeldRef.current = narration.state === "held";

  // spec R3.1.4 / R3.8.4: タブが hidden になったら continuous mode を pause、
  // visible に戻ったら resume。continuous 未起動でも pause/resume は noop で
  // 安全 (VadController が起動していないため中で何もしない)。
  useVisibilityPause({
    onHidden: () => {
      continuous.pause();
    },
    onVisible: () => {
      // 処理中は合図音が鳴っていてマイクを止めてある (useProcessingCue)。ここで戻すと
      // 鳴っている最中にマイクが開いてしまう。処理が終われば useProcessingCue の
      // 後始末が戻すので、ここでは何もしないのが正しい (spec 2026-07-27 §3.4)。
      //
      // 判定待ちの間も開けない (spec 2026-07-28 §3.8)。開けると、戻った瞬間に
      // AI の声とマイクが同時に立ち上がってまた誤って止まる。
      if (phaseRef.current === "processing" || narrationHeldRef.current) return;
      continuous.resume();
    },
  });

  const handleStartRecord = async () => {
    if (!survey) return;
    // continuous mode で mic 常時 ON、VAD が発話境界を判定する。
    // start は冪等 — VAD が生きていれば noop、死んでいれば作り直す。起動中の呼び出しは
    // 同じ promise を待つ (2026-08-02 まではこのコメントが実装と食い違っており、
    // タップのたびに VAD / SilenceTimer / Scribe が二重に立っていた)。
    try {
      await continuous.start();
      setMicState("listening");
    } catch (e) {
      setMicState("error");
      const msg = e instanceof AppError ? e.message : "マイクを使えませんでした";
      append({ kind: "ai-confirm", questionId: currentQid(), text: msg });
    }
  };

  // preflight でマイクは許可・取得済み (mediaStreamManager に live stream が載っている)。
  // 本編突入時にその stream をそのまま使って continuous を自動起動する = ユーザーの再タップ不要。
  // 「マイクは最初に一度 ON にしたら以降トグルしない」設計に合わせる (mic button は以降
  // 発話インジケータとして働く)。start は idempotent、失敗時は handleStartRecord が error 表示
  // → 🎙 タップで再試行できる。deps [survey, micEnabled] で本編突入時に一度走る (survey は
  // ロード時に一度だけ set され、以降 identity が安定)。?preflight=skip 経路は許可未取得なので
  // この自動 start が getUserMedia 許可を促す (dev 用、実運用は preflight で許可済み)。
  useEffect(() => {
    if (!survey || !micEnabled) return;
    void handleStartRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, micEnabled]);

  const handleChipPick = (n: number) => handleAnswer(n, "text");
  const handleText = (text: string) => handleAnswer(text, "text");

  const handleResume = () => {
    if (!survey) return;
    const restoredIds: string[] = [];
    for (const q of survey.questions) {
      const a = answers[q.id];
      if (!a || a.value === null || a.value === "") break;
      restoredIds.push(append({ kind: "ai-question", questionId: q.id, text: questionText(q) }));
      restoredIds.push(
        append({
          kind: "user-transcript",
          questionId: q.id,
          text: a.transcript ?? String(a.value),
        })
      );
      restoredIds.push(
        append({
          kind: "ai-confirm",
          questionId: q.id,
          text: confirmText(q, a.value),
        })
      );
    }
    // 復元した過去 bubble は読み上げない。**復元した分をすべて**記録する
    // (最後の 1 件だけでは、その手前が未読として読み上げられる)。
    narration.markSpoken(restoredIds);
    const idx = findFirstUnansweredIdx(survey, answers);
    setActiveIdx(idx);
    if (idx < survey.questions.length) {
      beginTurn(survey.questions[idx].id);
      append({
        kind: "ai-question",
        questionId: survey.questions[idx].id,
        text: questionText(survey.questions[idx]),
      });
      setPhase("asking");
    } else {
      showSummary();
    }
  };

  const handleStartOver = () => {
    if (!survey) return;
    const s = survey;
    useSurveyStore.getState().clear();
    // 回答ごと捨てる操作なので訂正履歴も一緒に捨てる。残すと新しいセッションの
    // answers.json に、もう存在しない回答への訂正が付いてくる。
    correctionsRef.current = {};
    setSurvey(s);
    clearChat();
    // 新しいセッションを開始 (voice-first: 失敗しても UI を止めない)
    void (async () => {
      const userId = await identityProvider.getUserId();
      const sessionId = crypto.randomUUID();
      useChatStore.getState().startSession({ sessionId });
      const startedAt = new Date().toISOString();
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
      sessionStartedAtRef.current = startedAt;
      sessionUserIdRef.current = userId;
      await sessionUploader.startSession({
        userId,
        sessionId,
        surveyId: s.id,
        userAgent,
        startedAt,
        ...(displayName ? { displayName } : {}),
      });
      // Task 8: handleStartOver も新規 session なので session.started の配線を揃える
      // (旧 session の sessionEventBuffer.attach を新 sessionId に張り直す)。
      // I2 fix: attach() は内部で detach() を呼び、前 session の buffered events
      // (wss.error / mic.acquire_failed 等) を silent discard する。ページが生きた
      // ままの controlled restart なので、その前に flush("error") でベストエフォート
      // upload してから新 session に attach し直す (attached が無ければ no-op)。
      sessionEventBuffer.flush("error");
      sessionEventBuffer.attach({ userId, sessionId, startedAt });
      eventBus.emit(
        makeEvent("session.started", sessionId, {
          userId,
          surveyId: s.id,
          startedAt,
          userAgent,
          viewport: { w: window.innerWidth, h: window.innerHeight },
          micGranted: micEnabled,
          // セッション単位の STT プロバイダ判別の一次キー (spec 2026-07-30 §3.7)
          sttProvider: resolveSttProvider(),
          previousBeaconFailed: readPreviousBeaconFailed(),
          ...(displayName ? { displayName } : {}),
        })
      );
    })();
    append({ kind: "ai-greeting", text: TTS_PHRASES.greeting });
    beginTurn(s.questions[0].id);
    append({
      kind: "ai-question",
      questionId: s.questions[0].id,
      text: questionText(s.questions[0]),
    });
    setActiveIdx(0);
    setPhase("asking");
  };

  /**
   * ✏️ 文字を直す (`?log=1` の会話ログからのみ到達する)。
   *
   * ロジックをここに置くのは、store 更新・R2 保存・計測が絡むため — 描画側
   * (ConversationLog) は入口を出すだけにして、保存の順序をこちらで守る。
   */
  const handleEditText = (messageId: string, questionId: string, before: string) => {
    const next = window.prompt("文字を直す", before);
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    useChatStore.getState().updateText(messageId, trimmed);
    setAnswer(questionId, trimmed, trimmed);
    // 保存と計測は store 更新の**後**に置く (spec §5.4)。ここは同期の
    // イベントハンドラなので、先に置いて throw すると訂正が画面に
    // 反映されないまま消える (原則 5)。
    //
    // 訂正の前後を持っているのはこの瞬間だけ — setAnswer が value と
    // transcript を同時に上書きするので、後からでは「STT が何と
    // 誤認識したか」を復元できない (spec D3)。
    const log = correctionsRef.current[questionId] ?? [];
    log.push({ at: new Date().toISOString(), before, after: trimmed, method: "edit-text" });
    correctionsRef.current[questionId] = log;
    const { sessionId: sid } = useChatStore.getState();
    if (sid) {
      saveAnswers(sid);
      // 本文は載せない (spec D5)。events.jsonl は 48KB trim の対象で、
      // 本文の正は cap の無い answers.json 側が持つ。turnId も付けない
      // (aggregateSummary が turnIds に足して turnCount が水増しされる)。
      eventBus.emit(
        makeEvent("answer.corrected", sid, {
          questionId,
          method: "edit-text",
          beforeLen: before.length,
          afterLen: trimmed.length,
        })
      );
    }
    // サマリフェーズ中の編集は asking に戻す (サマリカードを非表示にする)
    if (phase === "summary") {
      const idx = survey ? survey.questions.findIndex((q) => q.id === questionId) : -1;
      if (idx >= 0) {
        setActiveIdx(idx);
        setPhase("asking");
      }
    }
  };

  const handleRetry = (questionId: string) => {
    if (!survey) return;
    const idx = survey.questions.findIndex((q) => q.id === questionId);
    if (idx < 0) return;
    useChatStore.getState().removeUserTranscript(questionId);
    setActiveIdx(idx);
    setPhase("asking");
  };

  /**
   * 確認フォームの送信 (spec 2026-08-04 D4)。フォームの値を store へ反映し、
   * 計測を出してから通常の送信へ渡す。
   *
   * **`edited` と `filled` を分けて数えるのが要点** (§6.2) — 前者は「音声が外した」、
   * 後者は「音声が届かなかった」で、混ぜると認識精度の指標にならない。
   */
  const handleReviewSubmit = ({ values, feedback }: ReviewSubmission) => {
    if (!survey) return;
    const baseline = reviewBaselineRef.current;
    let edited = 0;
    let filled = 0;
    for (const q of survey.questions) {
      const before = baseline[q.id] ?? "";
      const after = (values[q.id] ?? "").trim();
      if (before === after) continue;
      if (before === "") {
        if (after !== "") filled += 1;
      } else {
        edited += 1;
        // 訂正の前後を残す (spec 2026-08-02 D3)。setAnswer は value と transcript を
        // 同時に上書きするので、ここで捕まえないと「STT が何と誤認識したか」が消える。
        const log = correctionsRef.current[q.id] ?? [];
        log.push({
          at: new Date().toISOString(),
          before,
          after,
          method: "edit-text",
        });
        correctionsRef.current[q.id] = log;
      }
      // 空にされた設問も含めて反映する (本人が消したなら未回答が正しい)
      setAnswer(q.id, after === "" ? null : after, after);
    }
    const sid = useChatStore.getState().sessionId;
    if (sid) {
      eventBus.emit(
        makeEvent("review.submitted", sid, {
          edited,
          filled,
          feedbackLen: feedback.length,
          durationMs: reviewEnteredAtRef.current
            ? Math.max(0, Date.now() - reviewEnteredAtRef.current)
            : 0,
        })
      );
    }
    handleSubmit(feedback);
  };

  const handleSubmit = (feedback?: string) => {
    if (!survey) return;
    // Task 8 (spec §6.2 #2, §7.2/§7.3): session.ended emit → finalizeManifest →
    // flush("submit") → detach の順で呼ぶ (CLAUDE.md 原則 5: fire-and-forget、
    // phase 遷移など UI 可視の完了はこのチェーンの完了を待たない)。
    const turns = survey.questions.length;
    // store から読み直す (buildAnswersArtifact と同じ理由)。確認フォームの送信は
    // setAnswer の直後に同期でここへ来るので、分割代入の answers は 1 つ前のまま。
    // 直した分が answered に反映されず、完了画面のドットが実際より少なくなる。
    const answered = Object.values(useSurveyStore.getState().answers).filter(
      (a) => a.value !== null && a.value !== ""
    ).length;
    const { sessionId } = useChatStore.getState();
    if (sessionId) {
      const skipped = turns - answered;
      const endedAt = new Date().toISOString();
      const startedAtMs = sessionStartedAtRef.current
        ? Date.parse(sessionStartedAtRef.current)
        : NaN;
      const durationMs = Number.isFinite(startedAtMs) ? Math.max(0, Date.now() - startedAtMs) : 0;
      eventBus.emit(
        makeEvent("session.ended", sessionId, {
          endedAt,
          status: "completed",
          reason: "submit",
          turns,
          answered,
          skipped,
          durationMs,
        })
      );
      // 送信時点のスナップショット (spec 2026-08-02 §5.3)。この下の clearStorage より
      // 前でなければならない — 消えるのは localStorage だけで store は生きているため
      // 順序を間違えても動いてしまうが、読み手に前提が伝わらない。
      // 感想はこの 1 回だけ載る (回答確定ごとの保存には存在しないため)。
      saveAnswers(sessionId, feedback);
      // re-arm (events.jsonl in-session 耐久性) で beaconSent が per-visible-episode に緩むため、
      // finalizeManifest の await 中に background すると abandoned beacon が完了 submit と競合する。
      // await より前に unload capture を同期 seal する (attached/subscription は残すので後続の
      // flush("submit") は正常保存)。spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §3.3。
      sessionEventBuffer.stopUnloadCapture();
      void (async () => {
        await sessionUploader.finalizeManifest({
          sessionId,
          status: "completed",
          endedAt,
        });
        sessionEventBuffer.flush("submit");
        sessionEventBuffer.detach();
      })();
    }
    // 送信完了時のローカル JSON ダウンロードは廃止 (R2 にログ保存されるため不要、2026-07-25)。
    // 回答は per-turn artifact + events.jsonl + summary.json として R2 に保存済み。
    // spec §3.10.3: 送信完了で mic 完全停止 (VAD destroy + Scribe close + track stop)。
    // continuous 未起動 (マイク拒否でテキスト完走) でも stop() は noop で安全。
    continuous.stop();
    // 回答の下書きを消す (spec 2026-07-26 D6)。消さないと送信済みなのに、次の訪問で
    // 「前回の続きから始めましょうか」と聞かれる。useSurveyStore.clear() ではなく
    // localStorage だけを消すのは、store から survey を落とすと完了画面ではなく
    // 「読み込み中…」になってしまうため。
    clearStorage(survey.id);
    saveCompletion({
      surveyId: survey.id,
      answered,
      total: turns,
      completedAt: new Date().toISOString(),
    });
    // 送信前の読み上げ (Review の確認) が残っていれば捨ててから、しめくくりを鳴らす。
    // 順序が逆だとキューを捨てる側がしめくくりまで巻き込む。
    narration.abandon();
    // 終わりだけ無音にしない (spec D4)。await しない (原則 5: 合成に失敗しても画面は進む)。
    // 送信時点でマイクは停止済みなので割り込みは起こらない。
    narration.speakNow(COMPLETION_TTS_ID, TTS_PHRASES.surveyCompleted);
    setStatus("submitted");
    setPhase("submitted");
  };

  if (!survey) {
    if (phase === "error")
      return <p className="p-4 text-red-600">読み込めませんでした: {errorMessage}</p>;
    return <p className="p-4">読み込み中…</p>;
  }

  const doneCount = Object.values(answers).filter((a) => a.value !== null && a.value !== "").length;

  // 送信済みは全画面で置き換える (spec 2026-07-26 D1)。ChatHeader も InputDock も描画しないので、
  // 「まだ何か答えるのか」と読めるマイク・入力欄が残らない。
  if (phase === "submitted") {
    return <Completion answered={doneCount} total={survey.questions.length} />;
  }

  // mic button は発話インジケータ (continuous は一度 ON にしたらトグルしない、MicButton
  // コメント参照): 起動後は VAD の hearing を "speaking" に反映して「今話しているか」を
  // 可視化する。error / 未起動 (idle) はそのまま、それ以外の起動中は listening を出す。
  // AI が読み上げている間は「どうぞ」と誘わない (spec 2026-07-28 D8)。チュートリアルで
  // 教えた「読み上げが終わったら、あなたの番」と画面が矛盾していると、話しかけを誘発する。
  // 判定待ち (held) は聞き取った内容を確かめている最中なので「聞いています」を出す。
  // 処理中 (spec 2026-08-04 D11) は「AI が話しています」の**後ろ**に置く — 処理中でも
  // 直前の吹き出しがまだ鳴り終わっていないことがあり、そのときは声の方が正しい。
  const micButtonState: MicState =
    micState === "error"
      ? "error"
      : micState === "idle"
        ? "idle"
        : narration.state === "speaking"
          ? "ai-speaking"
          : phase === "processing"
            ? "thinking"
            : narration.state === "held"
              ? "speaking"
              : continuous.vadState === "hearing"
                ? "speaking"
                : "listening";

  return (
    <div className="mx-auto w-full max-w-2xl flex flex-col flex-1 min-h-0 bg-slate-50">
      {/* ヘッダー (タイトル + 進捗ドット) も会話ログと一緒に隠す (spec D2 / U4)。
          「あと何問か」も画面から消える — 実機で不安が出るようなら U4 を見直す。 */}
      {showLog && (
        <ChatHeader title={survey.title} total={survey.questions.length} doneCount={doneCount} />
      )}
      {/* 後半はフォームだけ (spec D1)。マイクは showSummary で止めてあるので、
          画面からも消して「もう声では答えない」ことを見て分かるようにする。 */}
      {phase === "summary" ? (
        <ReviewForm survey={survey} answers={answers} onSubmit={handleReviewSubmit} />
      ) : (
        <VoiceStage
          micState={micButtonState}
          micDisabled={!micEnabled}
          inputDisabled={!["asking", "resume-prompt"].includes(phase)}
          forceTextFallback={forceTextFallback}
          onStartRecord={handleStartRecord}
          onSubmitText={handleText}
          compact={showLog}
        />
      )}
      {showLog && (
        <ConversationLog
          survey={survey}
          messages={messages}
          partialText={partialText}
          partialQuestionId={currentQid()}
          phase={phase}
          answers={answers}
          ttsState={narration.ttsState}
          onReplayTts={(id, text) => narration.speakNow(id, text)}
          onRetry={handleRetry}
          onEditText={handleEditText}
          chipExpanded={chipExpanded}
          onToggleChip={() => setChipExpanded((v) => !v)}
          forceChipFallback={forceChipFallback}
          onChipPick={handleChipPick}
          resumeButtonsExpanded={resumeButtonsExpanded}
          onToggleResumeButtons={() => setResumeButtonsExpanded((v) => !v)}
          forceResumeButtons={!micEnabled}
          onResume={handleResume}
          onStartOver={handleStartOver}
          containerRef={thread.containerRef}
          onScroll={thread.onScroll}
        />
      )}
      {/* spec §7 D7.1: ?vad-debug=1 で dev overlay を右下に描画。
          SSR/test 環境 (window undefined) をガード。 */}
      {typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("vad-debug") === "1" && (
          <VadDebugOverlay stream={mediaStreamManager.getStream()} vadState={continuous.vadState} />
        )}
    </div>
  );
}

function findFirstUnansweredIdx(
  survey: Survey,
  answers: Record<string, { value: number | string | null }>
): number {
  const idx = survey.questions.findIndex((q) => {
    const a = answers[q.id];
    return !a || a.value === null || a.value === "";
  });
  return idx < 0 ? survey.questions.length : idx;
}
